import React from "react";
import CancelOnTpReached from "features/tradingTerminal/General/CancelOnTpReached";
import SwitchSelect from "features/bots/BotForm/common/SwitchSelect";
import useLeverageSelectors from "features/tradingTerminal/General/useLeverageSelectors";
import useSharedGeneralTradeOptions from "@shared/features/tradingTerminal/common/hooks/useSharedGeneralTradeOptions";
import useConfirmModal from "common/Modal/useConfirmModal";
import { HookAPI } from "antd/es/modal/useModal";
import { useTheme } from "styled-components";
import SegmentedToggle, {
  SegmentedToggleTone,
} from "common/Segmented/SegmentedToggle";
import { Direction } from "@shared/types";

const DIRECTION_TO_TONE: Record<string, SegmentedToggleTone> = {
  [Direction.LONG]: "green",
  [Direction.SHORT]: "red",
};

const useGeneralTradeOptions = (confirmModal: HookAPI) => {
  const { openConfirmModal } = useConfirmModal({ confirmModal });
  const { isNewLook } = useTheme();
  // New look: small form choices use the segmented toggle.
  const Toggle = isNewLook ? SegmentedToggle : SwitchSelect;
  const {
    showGeneralOptions,
    leverageData,
    cancelOnTpData,
    longShortData,
    tradeTypeData,
    shouldDisplayLeverage,
  } = useSharedGeneralTradeOptions(openConfirmModal);

  const {
    marginTypeSelector,
    multiplierSelector,
    marginTypeErrorMessage,
    multiplierErrorMessage,
    leverageWarningModal,
  } = useLeverageSelectors(leverageData);

  const longShort = longShortData.shouldDisplay ? (
    <Toggle
      options={longShortData.options}
      selectedOption={longShortData.value}
      onClick={longShortData.onClick}
      {...(isNewLook && { optionToTone: DIRECTION_TO_TONE })}
    />
  ) : undefined;

  const leverage = shouldDisplayLeverage
    ? { marginTypeSelector, multiplierSelector }
    : undefined;

  const tradeType = (
    <Toggle
      options={tradeTypeData.options}
      selectedOption={tradeTypeData.value}
      onClick={tradeTypeData.onClick}
    />
  );

  const cancelOnTp = cancelOnTpData.shouldDisplay ? (
    <CancelOnTpReached
      value={cancelOnTpData.value}
      onChange={cancelOnTpData.onChange}
      isBreakout={cancelOnTpData.isBreakout}
      errorMessage={cancelOnTpData.errorMessage}
    />
  ) : undefined;

  return {
    showGeneralOptions,
    longShort,
    leverage,
    tradeType,
    // New look: an option-less toggle renders as an empty pill, so the field
    // is left out instead.
    showTradeType: !isNewLook || tradeTypeData.options.length > 0,
    cancelOnTp,
    marginTypeErrorMessage,
    multiplierErrorMessage,
    marginErrorReason: leverageData.errorMessageReason,
    leverageWarningModal,
  };
};

export default useGeneralTradeOptions;
